import { Formik } from 'formik';
import { useState } from 'react';
import AddComingForm from './AddComingForm';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import type { LeadCloudFileType } from '@econtractor/types';

const emptyComingValues = {
    cCount: '',
    price: 0,
    priceSale: 0,
    priceRate: 0,
    priceSaleRate: 0,
    cDescription: '',
};

interface AddComingFormikProps {
    cProduct: Record<string, any>;
    setAComing: React.Dispatch<React.SetStateAction<any[]>>;
    setAddProduct: (value: null) => void;
    updateCatalogPrice: boolean;
    onUpdateCatalogPriceChange: (checked: boolean) => void;
}

const AddComingFormik = ({
    cProduct,
    setAComing,
    setAddProduct,
    updateCatalogPrice,
    onUpdateCatalogPriceChange,
}: AddComingFormikProps) => {
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const canCredit = Boolean(params.creditControl);
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const rate = localSettings?.RATE?.current || 1;
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);

    const uploadCloudFiles = async (productId: number): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            productId: Number(productId || 0),
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(pendingCloudFiles, uploadConfigs, setCloudProgress);
        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    return (
        <div className="px-3">
            <Formik
                key={cProduct?.value}
                enableReinitialize
                initialValues={{
                    ...emptyComingValues,
                    price: cProduct?.defPrice || 0,
                    priceRate: rate * (cProduct?.defPrice || 0),
                    priceSale: cProduct?.defPriceSale || 0,
                    priceSaleRate: rate * (cProduct?.defPriceSale || 0),
                    payment: false,
                    paymentDescription: '',
                }}
                onSubmit={async (values, { resetForm }) => {
                    let paymentAttachmentsCloud: LeadCloudFileType[] = [];
                    const payment = canCredit && Boolean(values.payment);

                    if (payment && pendingCloudFiles.length) {
                        try {
                            setCloudProgress(1);
                            paymentAttachmentsCloud = await uploadCloudFiles(Number(cProduct?.value || 0));
                            if (paymentAttachmentsCloud.length !== pendingCloudFiles.length) {
                                showToast(t('common:messError'), 'warning');
                            }
                        } catch (e) {
                            console.error(e);
                            showToast(t('common:messError'), 'danger');
                            setCloudProgress(false);
                            return;
                        } finally {
                            setCloudProgress(false);
                        }
                    }

                    const ignorePrices = Boolean(params.ignoreWarehousePrice);
                    setAComing((prev) => [
                        ...prev,
                        {
                            setId: 0,
                            product: {
                                ...cProduct,
                                ID: cProduct.value,
                            },
                            coming: {
                                ...values,
                                price: ignorePrices ? 0 : values.price,
                                priceRate: ignorePrices ? 0 : values.priceRate,
                                priceSale: ignorePrices ? 0 : values.priceSale,
                                priceSaleRate: ignorePrices ? 0 : values.priceSaleRate,
                                payment,
                                paymentDescription: payment ? values.paymentDescription : '',
                                paymentAttachmentsCloud: payment ? paymentAttachmentsCloud : [],
                                updateCatalogPrice: !ignorePrices && updateCatalogPrice,
                            },
                        },
                    ]);
                    setPendingCloudFiles([]);
                    resetForm();
                    setAddProduct(null);
                }}
            >
                {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
                    <AddComingForm
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        values={values}
                        onCancel={() => setAddProduct(null)}
                        pendingCloudFiles={pendingCloudFiles}
                        setPendingCloudFiles={setPendingCloudFiles}
                        cloudProgress={cloudProgress}
                        updateCatalogPrice={updateCatalogPrice}
                        onUpdateCatalogPriceChange={onUpdateCatalogPriceChange}
                    />
                )}
            </Formik>
        </div>
    );
};

export default AddComingFormik;
